import _ from 'underscore';
import type { Dispatcher } from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import type { PeerReviewSubmission } from 'bundles/peer-admin/types/PeerReviewSubmission';

const SERIALIZED_PROPS: Array<keyof PeerAdminStoreDehydratedState> = [
  'rows',
  'next',
  'total',
  'hasNext',
  'hasPrevious',
];

type PeerAdminStoreDehydratedState = {
  rows: Array<PeerReviewSubmission>;
  next: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
};

/**
 * PeerAdminStore manages the state for peer review submissions in the peer admin interface.
 * It handles receiving, storing, and paginating rows of peer review submissions, and exposes methods
 * to access the current rows, pagination state, and total counts. The store is designed to work with
 * Fluxible's dispatcher and supports serialization and deserialization for server/client rendering.
 */
class PeerAdminStore extends BaseStore {
  static storeName = 'PeerAdminStore';

  static handlers = {
    RECEIVE_ROWS: 'onReceiveRows',
    RECEIVE_RANGE: 'onReceiveRange',
  };

  onReceiveRows(rows: Array<PeerReviewSubmission>) {
    this.rows = rows;
    this.emitChange();
  }

  onReceiveRange({ next, total, start }: { next: number; total: number; start: number }) {
    this.next = next;
    this.total = total;

    this.hasPrevious = start > 0;
    this.hasNext = next < this.total;

    this.emitChange();
  }

  declare rows: Array<PeerReviewSubmission>;

  declare next: number;

  declare total: number;

  declare hasNext: boolean;

  declare hasPrevious: boolean;

  constructor(dispatcher: Dispatcher) {
    super(dispatcher);

    this.rows = [];
    this.next = 0;
    this.total = 0;
    this.hasNext = false;
    this.hasPrevious = false;
  }

  dehydrate(): PeerAdminStoreDehydratedState {
    return _(this).pick(...SERIALIZED_PROPS);
  }

  rehydrate(state: PeerAdminStoreDehydratedState) {
    Object.assign(this, _(state).pick(...SERIALIZED_PROPS));
  }

  getRows(): Array<PeerReviewSubmission> {
    return this.rows;
  }

  hasNextPage(): boolean {
    return this.hasNext;
  }

  hasPreviousPage(): boolean {
    return this.hasPrevious;
  }

  getTotal(): number {
    return this.total;
  }

  getNext(): number {
    return this.next;
  }
}

export default PeerAdminStore;
